import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  updateConsent,
  CookiePreferences,
  trackEvent,
  getStoredConsent,
  saveConsent,
  logConsentToBackend,
} from "@/lib/analytics";

interface CookieConsentProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CookieConsent = ({ isOpen: controlledOpen, onOpenChange }: CookieConsentProps) => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    advertising: false,
  });

  const isControlledInstance = controlledOpen !== undefined;
  const isPreferencesOpen = controlledOpen !== undefined ? controlledOpen : showPreferences;
  const setIsPreferencesOpen = onOpenChange || setShowPreferences;

  useEffect(() => {
    // null = missing, malformed, legacy format, version-bumped, or expired
    const stored = getStoredConsent();

    if (!stored) {
      if (!isControlledInstance) {
        setShowBanner(true);
      }
      return;
    }

    setPreferences(stored.preferences);
    updateConsent(stored.preferences);
  }, [isControlledInstance]);

  const persist = (
    newPreferences: CookiePreferences,
    choice: "accept_all" | "reject_non_essential" | "custom",
  ) => {
    saveConsent(newPreferences);
    setPreferences(newPreferences);
    updateConsent(newPreferences);
    trackEvent("cookie_consent", {
      choice,
      analytics: newPreferences.analytics,
    });
    void logConsentToBackend(choice, newPreferences);
  };

  const handleAcceptAll = () => {
    persist({ necessary: true, analytics: true, advertising: false }, "accept_all");
    setShowBanner(false);
    setIsPreferencesOpen(false);
  };

  const handleRejectNonEssential = () => {
    persist({ necessary: true, analytics: false, advertising: false }, "reject_non_essential");
    setShowBanner(false);
    setIsPreferencesOpen(false);
  };

  const handleSavePreferences = () => {
    persist(
      { necessary: true, analytics: preferences.analytics, advertising: false },
      "custom",
    );
    setIsPreferencesOpen(false);
    setShowBanner(false);
  };

  const handlePreferencesOpenChange = (open: boolean) => {
    if (!open) {
      handleSavePreferences();
    }
    setIsPreferencesOpen(open);
  };

  const openPreferences = () => {
    setShowBanner(false);
    setIsPreferencesOpen(true);
  };

  return (
    <>
      {/* Bottom Banner */}
      {showBanner && !isControlledInstance && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg">
          <div className="container mx-auto px-4 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1 text-sm text-foreground">
                <p className="mb-2">
                  {t("cookieConsent.banner.message")}{" "}
                  <Link to="/privacy-policy" className="text-primary underline hover:text-primary/80">
                    {t("cookieConsent.banner.policyLink")}
                  </Link>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <Button onClick={handleAcceptAll} variant="accept" size="sm" className="whitespace-nowrap w-full sm:w-auto">
                  {t("cookieConsent.banner.acceptAll")}
                </Button>
                <Button onClick={handleRejectNonEssential} variant="decline" size="sm" className="whitespace-nowrap w-full sm:w-auto">
                  {t("cookieConsent.banner.rejectNonEssential")}
                </Button>
                <Button onClick={openPreferences} variant="tertiary" size="sm" className="whitespace-nowrap w-full sm:w-auto">
                  {t("cookieConsent.banner.preferences")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      <Dialog open={isPreferencesOpen} onOpenChange={handlePreferencesOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("cookieConsent.dialog.title")}</DialogTitle>
            <DialogDescription className="pt-2">
              {t("cookieConsent.dialog.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Necessary Cookies */}
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-border">
              <div className="flex-1 space-y-1">
                <Label htmlFor="necessary" className="text-base font-semibold cursor-pointer">
                  {t("cookieConsent.dialog.necessary.label")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t("cookieConsent.dialog.necessary.description")}
                </p>
              </div>
              <Switch id="necessary" checked={preferences.necessary} disabled className="mt-1" />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-1">
                <Label htmlFor="analytics" className="text-base font-semibold cursor-pointer">
                  {t("cookieConsent.dialog.analytics.label")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t("cookieConsent.dialog.analytics.description")}
                </p>
              </div>
              <Switch
                id="analytics"
                checked={preferences.analytics}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, analytics: checked })
                }
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
            <Button onClick={handleAcceptAll} variant="accept" className="flex-1">
              {t("cookieConsent.dialog.acceptAll")}
            </Button>
            <Button onClick={handleRejectNonEssential} variant="decline" className="flex-1">
              {t("cookieConsent.dialog.rejectNonEssential")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
